(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-subscription-popup]';
  const CLOSE_SELECTOR = '[data-subscription-popup-close]';
  const DISMISS_SELECTOR = '[data-subscription-popup-dismiss]';
  const OVERLAY_SELECTOR = '[data-subscription-popup-overlay]';

  const LOCAL_LOCK_ATTR = 'data-theme-subscription-popup-lock-count';
  const LOCAL_LOCK_CLASS = 'theme-subscription-popup-lock';
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const DEFAULT_CLOSE_DURATION = 520;

  const controllers = new Map();

  const parseDurationMs = (rawValue) => {
    if (!rawValue) return NaN;
    const value = String(rawValue).trim();
    if (value.endsWith('ms')) return Number.parseFloat(value);
    if (value.endsWith('s')) return Number.parseFloat(value) * 1000;
    return Number.parseFloat(value);
  };

  const getScrollbarCompensation = () =>
    Math.max(0, window.innerWidth - document.documentElement.clientWidth);

  const acquireGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      const compensation = `${getScrollbarCompensation()}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      if (body) body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      if (body) body.classList.add(GLOBAL_LOCK_CLASS);
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(current + 1));
  };

  const releaseGlobalScrollLock = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      if (body) body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      if (body) body.style.removeProperty(SCROLLBAR_VAR);
      return;
    }

    root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
  };

  const lockScroll = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);

    if (current <= 0) {
      root.classList.add(LOCAL_LOCK_CLASS);
      if (body) body.classList.add(LOCAL_LOCK_CLASS);
      acquireGlobalScrollLock();
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(current + 1));
  };

  const unlockScroll = () => {
    const root = document.documentElement;
    const body = document.body;
    const current = Number.parseInt(root.getAttribute(LOCAL_LOCK_ATTR) || '0', 10);
    const next = Math.max(0, current - 1);

    if (next === 0) {
      root.classList.remove(LOCAL_LOCK_CLASS);
      if (body) body.classList.remove(LOCAL_LOCK_CLASS);
      root.removeAttribute(LOCAL_LOCK_ATTR);
      releaseGlobalScrollLock();
      return;
    }

    root.setAttribute(LOCAL_LOCK_ATTR, String(next));
  };

  class SubscriptionPopupController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.enabled = root.dataset.enabled === 'true';
      this.triggerMode = root.dataset.trigger || 'delay';
      this.delayMs = Math.max(0, Number.parseInt(root.dataset.delay || '0', 10) * 1000);
      this.showOnce = root.dataset.showOnce === 'true';
      this.dismissKey = root.dataset.dismissKey || '';
      this.closeOnOverlay = root.dataset.closeOnOverlay === 'true';
      this.forceOpen = root.dataset.forceOpen === 'true';
      this.designMode = root.dataset.designMode === 'true';

      this.closeButtons = Array.from(root.querySelectorAll(CLOSE_SELECTOR));
      this.dismissButton = root.querySelector(DISMISS_SELECTOR);
      this.overlay = root.querySelector(OVERLAY_SELECTOR);

      this.isOpen = false;
      this.openTimer = null;
      this.closeTimer = null;

      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDismissClick = this.onDismissClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onExternalOpen = this.onExternalOpen.bind(this);
      this.onExternalClose = this.onExternalClose.bind(this);
    }

    init() {
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.onCloseClick);
      });

      if (this.dismissButton) {
        this.dismissButton.addEventListener('click', this.onDismissClick);
      }

      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.addEventListener('theme:subscription-popup:open', this.onExternalOpen);
      document.addEventListener('theme:subscription-popup:close', this.onExternalClose);

      if (this.shouldAutoOpen()) {
        this.scheduleOpen();
      }
    }

    destroy() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);

      this.closeButtons.forEach((button) => {
        button.removeEventListener('click', this.onCloseClick);
      });

      if (this.dismissButton) {
        this.dismissButton.removeEventListener('click', this.onDismissClick);
      }

      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.removeEventListener('theme:subscription-popup:open', this.onExternalOpen);
      document.removeEventListener('theme:subscription-popup:close', this.onExternalClose);

      if (this.isOpen) {
        this.isOpen = false;
        if (!this.designMode) {
          unlockScroll();
        }
      }

      this.root.classList.remove('is-open', 'is-opening', 'is-closing');
      this.root.setAttribute('aria-hidden', 'true');
      this.root.setAttribute('hidden', '');
    }

    shouldAutoOpen() {
      if (!this.enabled && !this.forceOpen) return false;

      if (this.designMode) return false;
      if (this.forceOpen) return true;

      if (this.showOnce && this.isDismissed()) return false;
      if (this.triggerMode === 'manual') return false;

      return true;
    }

    scheduleOpen() {
      window.clearTimeout(this.openTimer);

      const openDelay = this.forceOpen || this.designMode || this.triggerMode === 'immediate' ? 0 : this.delayMs;
      this.openTimer = window.setTimeout(() => this.open(), openDelay);
    }

    open() {
      if (this.isOpen) return;

      if (!this.forceOpen && !this.designMode && this.showOnce && this.isDismissed()) {
        return;
      }

      window.clearTimeout(this.closeTimer);
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      this.root.classList.remove('is-closing');
      this.root.classList.add('is-opening');

      requestAnimationFrame(() => {
        this.root.classList.remove('is-opening');
        this.root.classList.add('is-open');
      });

      if (!this.designMode) {
        lockScroll();
      }
      this.isOpen = true;

      if (this.forceOpen && this.showOnce) {
        this.markDismissed();
      }
    }

    close({ persist = true } = {}) {
      if (!this.isOpen && !this.root.classList.contains('is-open')) {
        this.root.setAttribute('aria-hidden', 'true');
        this.root.setAttribute('hidden', '');
        return;
      }

      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);

      this.root.classList.remove('is-opening');
      this.root.classList.remove('is-open');
      this.root.classList.add('is-closing');
      this.root.setAttribute('aria-hidden', 'true');
      this.isOpen = false;

      if (!this.designMode) {
        unlockScroll();
      }

      if (persist) {
        this.markDismissed();
      }

      const closeDuration = this.getCloseDuration();
      this.closeTimer = window.setTimeout(() => {
        this.root.classList.remove('is-closing');
        this.root.setAttribute('hidden', '');
      }, closeDuration);
    }

    getCloseDuration() {
      const duration = parseDurationMs(
        getComputedStyle(this.root).getPropertyValue('--theme-modal-close-duration')
      );
      return Number.isFinite(duration) && duration > 0 ? duration : DEFAULT_CLOSE_DURATION;
    }

    isDismissed() {
      if (!this.showOnce || !this.dismissKey) return false;

      try {
        return window.sessionStorage.getItem(this.dismissKey) === '1';
      } catch (error) {
        return false;
      }
    }

    markDismissed() {
      if (!this.showOnce || !this.dismissKey) return;

      try {
        window.sessionStorage.setItem(this.dismissKey, '1');
      } catch (error) {
        // Ignore storage write errors (private mode, blocked storage, etc.).
      }
    }

    onCloseClick(event) {
      const target = event.currentTarget;
      if (target === this.overlay && !this.closeOnOverlay) return;
      event.preventDefault();
      this.close({ persist: true });
    }

    onDismissClick(event) {
      event.preventDefault();
      this.close({ persist: true });
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      event.stopPropagation();
      this.close({ persist: true });
    }

    onSectionSelect(event) {
      if (!event.detail || event.detail.sectionId !== this.sectionId) return;
      this.open();
    }

    onSectionDeselect(event) {
      if (!event.detail || event.detail.sectionId !== this.sectionId) return;
      if (!this.enabled) {
        this.close({ persist: false });
      }
    }

    onExternalOpen(event) {
      const targetSectionId = event.detail && event.detail.sectionId;
      if (targetSectionId && targetSectionId !== this.sectionId) return;
      this.open();
    }

    onExternalClose(event) {
      const targetSectionId = event.detail && event.detail.sectionId;
      if (targetSectionId && targetSectionId !== this.sectionId) return;
      this.close({ persist: false });
    }
  }

  const initPopup = (root) => {
    if (!(root instanceof HTMLElement)) return;
    if (controllers.has(root)) return;

    const controller = new SubscriptionPopupController(root);
    controller.init();
    controllers.set(root, controller);
  };

  const initWithin = (container) => {
    if (!(container instanceof Element || container instanceof Document)) return;

    if (container instanceof Element && container.matches(ROOT_SELECTOR)) {
      initPopup(container);
    }

    container.querySelectorAll(ROOT_SELECTOR).forEach((root) => initPopup(root));
  };

  const destroyBySectionId = (sectionId) => {
    if (!sectionId) return;

    controllers.forEach((controller, root) => {
      if (controller.sectionId !== sectionId) return;
      controller.destroy();
      controllers.delete(root);
    });
  };

  if (!window.__themeSubscriptionPopupBound) {
    window.__themeSubscriptionPopupBound = true;

    document.addEventListener('shopify:section:load', (event) => {
      initWithin(event.target || document);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroyBySectionId(event.detail && event.detail.sectionId);
    });
  }

  initWithin(document);
})();
